import type { HeadToHeadOutcome, HeadToHeadPlayerResult } from '@ranked-waiting/stack-tower-core';
import { escapeHtml } from './format';
import { isStackTowerAudioMuted, setStackTowerAudioMuted } from './stack-tower-audio';
import { checkForNewAchievements } from './achievement-toast';
import {
  mountOpponentTowerEngine,
  mountOwnTowerEngine,
  type OpponentTowerEngine,
  type OwnTowerEngine,
  type StackTowerPlaceEvent,
} from './stack-tower-match-engine';

/**
 * C015 Phase 3-5 — wires a connected `StackTowerMatch` DO WebSocket to the
 * lobby → countdown → split-screen match → result UI. Structurally the same
 * idea as `duel-session.ts` (a `uiState` machine driven by server messages,
 * a disconnect banner that lives outside the main content so it never wipes
 * a live match), but the mechanic is different enough — a shared
 * authoritative clock instead of round-by-round messages, two live canvases
 * instead of one — that this is its own module rather than a variant of that
 * one.
 */

type PlayerSlot = 'a' | 'b';

/** C017 Phase 3 — 'endless' has no shared clock (spec §106-107); 'timed' is C015's original 60s window. Absent on messages predating Phase 3 is impossible (the server always sends it now), but a default keeps a stray malformed frame from crashing the session. */
type StackTowerMatchMode = 'timed' | 'endless';

interface JoinedMessage {
  type: 'joined';
  slot: PlayerSlot;
  you: string;
  opponent: string | null;
  mode: StackTowerMatchMode;
}
interface OpponentJoinedMessage {
  type: 'opponent-joined';
  name: string;
}
interface PresenceMessage {
  type: 'presence';
  phase: 'waiting' | 'playing' | 'finished';
  connected: Record<PlayerSlot, boolean>;
  names: Record<PlayerSlot, string | null>;
  ready: Record<PlayerSlot, boolean>;
}
interface CountdownMessage {
  type: 'countdown';
  matchStartMs: number;
  /** null for endless mode (spec §106-107) — there is no shared window to render a timer for. */
  durationMs: number | null;
  countdownMs: number;
}
interface OpponentPlaceMessage {
  type: 'opponent-place';
  event: StackTowerPlaceEvent;
}
interface ResultMessage {
  type: 'result';
  winner: HeadToHeadOutcome;
  a: HeadToHeadPlayerResult;
  b: HeadToHeadPlayerResult;
  /** C015 Phase 6. Empty for a casual match — ranked status is derived server-side, never client-declared (D2). */
  ratingDeltas?: Partial<Record<PlayerSlot, number>>;
}
interface OpponentLeftMessage {
  type: 'opponent-left';
}
interface RematchWantedMessage {
  type: 'rematch-wanted';
  slot: PlayerSlot;
}
/** Sent once this slot's socket closed mid-match — a grace window has started (C015 Phase 5). */
interface OpponentDisconnectedMessage {
  type: 'opponent-disconnected';
  graceMs: number;
}
/** Sent once the disconnected slot reconnects within its grace window. */
interface OpponentReconnectedMessage {
  type: 'opponent-reconnected';
}
/** Sent to a socket that reconnected to a slot still within its grace window — enough to rebuild the live match in progress. */
interface ResumedMessage {
  type: 'resumed';
  slot: PlayerSlot;
  you: string;
  opponent: string | null;
  mode: StackTowerMatchMode;
  matchStartMs: number;
  /** Always TIMED_MATCH_DURATION_MS today — the DO sends the timed-mode constant unconditionally on `resumed` even for an endless match (it's meaningless there; the client only uses it when `mode === 'timed'`). */
  durationMs: number;
  myScore: number;
  opponentScore: number;
  /** Endless mode only — set if the opponent had already finished before the disconnect, so a reconnecting client can rebuild the "N TO WIN" state too. */
  opponentFinalScore: number | null;
  /** Endless mode only — did *this* player already finish before disconnecting? A finished-but-still-connected player waiting on the opponent is a real, reachable state there. */
  myFinished: boolean;
}
/** Endless mode only (C017 Phase 3, spec §106-107): the opponent just missed — their tower is now a frozen "monument" at this score. */
interface OpponentFinishedMessage {
  type: 'opponent-finished';
  finalScore: number;
}
/** C015 Phase 6 — sent instead of starting a rematch once a ranked pair has already used their one allowed rematch. */
interface RequeueRequiredMessage {
  type: 'requeue-required';
  message: string;
}
type ServerMessage =
  | JoinedMessage
  | OpponentJoinedMessage
  | PresenceMessage
  | CountdownMessage
  | OpponentPlaceMessage
  | ResultMessage
  | OpponentLeftMessage
  | RematchWantedMessage
  | OpponentDisconnectedMessage
  | OpponentReconnectedMessage
  | ResumedMessage
  | OpponentFinishedMessage
  | RequeueRequiredMessage;

export interface StackTowerMatchSessionOptions {
  lobbyHeading: string;
  onExit: () => void;
}

function send(socket: WebSocket, message: unknown): void {
  if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function attachStackTowerMatchSession(
  mount: HTMLElement,
  socket: WebSocket,
  options: StackTowerMatchSessionOptions,
): () => void {
  let mySlot: PlayerSlot | null = null;
  let myName = '';
  let opponentName: string | null = null;
  let opponentPresent = false;
  let destroyed = false;
  let uiState: 'lobby' | 'countdown' | 'live' | 'waiting-result' | 'result' | 'opponent-left' | 'error' = 'lobby';

  let countdownTimerId: number | undefined;
  let liveTimerId: number | undefined;
  let liveTransitionId: number | undefined;
  let ownEngine: OwnTowerEngine | null = null;
  let opponentEngine: OpponentTowerEngine | null = null;
  let myScore = 0;
  let opponentScore = 0;
  let matchMode: StackTowerMatchMode = 'timed';
  // Endless mode only (C017 Phase 3) — which side, if either, has already
  // missed and become a frozen "monument". The match keeps running (still
  // 'live') until either both have finished or one surpasses the other's
  // frozen score — see the DO's own comment for why that's checked
  // server-side on every placement, not just here.
  let myFinished = false;
  let opponentFinished = false;
  let opponentFinalScore: number | null = null;

  // A banner that lives outside the content area so a mid-match disconnect
  // notice never wipes the live split-screen match (or any other screen) —
  // every render function below targets `contentEl`, never `mount` directly.
  // Same structural fix duel-session.ts already relies on.
  mount.innerHTML = `
    <div class="connection-banner" id="st-connection-banner" hidden></div>
    <div class="stack-tower-session-content" id="st-session-content"></div>
  `;
  const bannerEl = mount.querySelector<HTMLDivElement>('#st-connection-banner')!;
  const contentEl = mount.querySelector<HTMLDivElement>('#st-session-content')!;

  let graceDeadlineMs: number | null = null;
  let bannerIntervalId: number | undefined;

  function renderBanner(): void {
    if (graceDeadlineMs === null) {
      bannerEl.hidden = true;
      bannerEl.textContent = '';
      return;
    }
    const remainingSeconds = Math.max(0, Math.ceil((graceDeadlineMs - Date.now()) / 1000));
    bannerEl.hidden = false;
    bannerEl.textContent = `Opponent disconnected. Reconnecting in ${remainingSeconds}s…`;
  }

  function startDisconnectBanner(graceMs: number): void {
    graceDeadlineMs = Date.now() + graceMs;
    renderBanner();
    if (bannerIntervalId !== undefined) window.clearInterval(bannerIntervalId);
    bannerIntervalId = window.setInterval(renderBanner, 1000);
  }

  function clearDisconnectBanner(): void {
    graceDeadlineMs = null;
    if (bannerIntervalId !== undefined) {
      window.clearInterval(bannerIntervalId);
      bannerIntervalId = undefined;
    }
    renderBanner();
  }

  function clearTimers(): void {
    if (countdownTimerId !== undefined) window.clearInterval(countdownTimerId);
    if (liveTimerId !== undefined) window.clearInterval(liveTimerId);
    if (liveTransitionId !== undefined) window.clearTimeout(liveTransitionId);
    countdownTimerId = undefined;
    liveTimerId = undefined;
    liveTransitionId = undefined;
  }

  function destroyEngines(): void {
    ownEngine?.destroy();
    opponentEngine?.destroy();
    ownEngine = null;
    opponentEngine = null;
  }

  function renderLobby(presence?: PresenceMessage): void {
    const meReady = mySlot && presence ? presence.ready[mySlot] : false;
    const oppReady = mySlot && presence ? presence.ready[mySlot === 'a' ? 'b' : 'a'] : false;
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">${escapeHtml(options.lobbyHeading)}</p>
        <p class="round-stage__hint">${opponentPresent ? `${escapeHtml(opponentName ?? 'Opponent')} connected.` : 'Waiting for opponent to join…'}</p>
        ${oppReady ? `<p class="round-stage__hint">Opponent is ready.</p>` : ''}
        <button class="button button--primary" type="button" data-action="ready" ${meReady ? 'disabled' : ''}>
          ${meReady ? 'Waiting for opponent…' : "I'm ready"}
        </button>
        <button class="button button--secondary" type="button" data-action="exit">Back to menu</button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="ready"]')?.addEventListener('click', () => {
      send(socket, { type: 'ready' });
    });
    contentEl.querySelector<HTMLButtonElement>('[data-action="exit"]')?.addEventListener('click', () => options.onExit());
  }

  function renderCountdown(countdownMs: number, matchStartMs: number): void {
    const ruleHint =
      matchMode === 'endless'
        ? 'no clock — a miss ends your run, surpass their score or outlast them to win.'
        : '60 seconds, highest score wins.';
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Get ready</p>
        <h2 class="stack-tower-countdown__number" id="st-countdown-number">${Math.ceil(countdownMs / 1000)}</h2>
        <p class="round-stage__hint">${escapeHtml(myName)} vs ${escapeHtml(opponentName ?? 'Opponent')}. ${ruleHint}</p>
      </div>
    `;
    const numberEl = contentEl.querySelector<HTMLHeadingElement>('#st-countdown-number')!;
    const tick = () => {
      const remainingMs = matchStartMs - Date.now();
      numberEl.textContent = remainingMs > 0 ? String(Math.ceil(remainingMs / 1000)) : 'STACK!';
    };
    tick();
    countdownTimerId = window.setInterval(tick, 100);
  }

  function renderLiveShell(durationMs: number | null): { ownMount: HTMLDivElement; opponentMount: HTMLDivElement } {
    // Timed mode shows the shared clock in the middle slot; endless mode
    // (spec §106-107, no clock) shows a live "N TO WIN" / waiting status
    // there instead, filled in by updateTargetDisplay() below.
    const middleSlot =
      durationMs !== null
        ? `<span class="stack-tower-versus__timer" id="st-timer">${formatClock(durationMs)}</span>`
        : `<span class="stack-tower-versus__timer" id="st-target"></span>`;
    contentEl.innerHTML = `
      <div class="stack-tower-versus" data-lead="tied">
        <button class="stack-tower-mute-button" id="st-mute-button" type="button" aria-label="Mute sound">${isStackTowerAudioMuted() ? '🔇' : '🔊'}</button>
        <div class="stack-tower-versus__bar">
          <span class="stack-tower-versus__name" data-side="me">${escapeHtml(myName)} <strong id="st-my-score">0</strong><span class="stack-tower-versus__mult" id="st-my-mult" hidden></span><span class="stack-tower-versus__finished" id="st-my-finished" hidden>FINISHED</span></span>
          ${middleSlot}
          <span class="stack-tower-versus__name" data-side="opponent">${escapeHtml(opponentName ?? 'Opponent')} <strong id="st-opp-score">0</strong><span class="stack-tower-versus__finished" id="st-opp-finished" hidden>FINISHED</span></span>
        </div>
        <div class="stack-tower-versus__panes">
          <div class="stack-tower-mount stack-tower-versus__pane" id="st-own-pane"></div>
          <div class="stack-tower-mount stack-tower-versus__pane" id="st-opp-pane"></div>
        </div>
        <p class="stack-tower-milestone" id="st-milestone" aria-hidden="true"></p>
      </div>
    `;
    const muteButton = contentEl.querySelector<HTMLButtonElement>('#st-mute-button')!;
    muteButton.addEventListener('click', () => {
      setStackTowerAudioMuted(!isStackTowerAudioMuted());
      muteButton.textContent = isStackTowerAudioMuted() ? '🔇' : '🔊';
    });
    return {
      ownMount: contentEl.querySelector<HTMLDivElement>('#st-own-pane')!,
      opponentMount: contentEl.querySelector<HTMLDivElement>('#st-opp-pane')!,
    };
  }

  function updateLead(): void {
    const bar = contentEl.querySelector<HTMLDivElement>('.stack-tower-versus');
    if (!bar) return;
    bar.dataset.lead = myScore === opponentScore ? 'tied' : myScore > opponentScore ? 'me' : 'opponent';
  }

  /**
   * Starts (or, given `resumeScores`, rebuilds) the live split-screen match.
   * A reconnect (Phase 5) skips the countdown entirely — the authoritative
   * window is already running — and seeds both panes' scores from the
   * server's last-known values instead of 0, exactly mirroring how a timed
   * *miss* already carries score through a tower-geometry reset: to a
   * reconnecting client, "my tower is gone but my score is not" is the same
   * shape of event either way.
   */
  function startLiveMatch(
    matchStartMs: number,
    durationMs: number | null,
    resumeScores?: { mine: number; opponent: number },
    resumeFinished?: { mine: boolean; opponentFinalScore: number | null },
  ): void {
    if (destroyed) return;
    clearTimers();
    destroyEngines();
    uiState = 'live';
    myScore = resumeScores?.mine ?? 0;
    opponentScore = resumeScores?.opponent ?? 0;
    myFinished = resumeFinished?.mine ?? false;
    opponentFinalScore = resumeFinished?.opponentFinalScore ?? null;
    opponentFinished = opponentFinalScore !== null;
    const { ownMount, opponentMount } = renderLiveShell(durationMs);
    const myScoreEl = contentEl.querySelector<HTMLElement>('#st-my-score')!;
    const myMultEl = contentEl.querySelector<HTMLElement>('#st-my-mult')!;
    const myFinishedEl = contentEl.querySelector<HTMLElement>('#st-my-finished')!;
    const oppScoreEl = contentEl.querySelector<HTMLElement>('#st-opp-score')!;
    const oppFinishedEl = contentEl.querySelector<HTMLElement>('#st-opp-finished')!;
    const milestoneEl = contentEl.querySelector<HTMLParagraphElement>('#st-milestone')!;
    myScoreEl.textContent = String(myScore);
    oppScoreEl.textContent = String(opponentScore);
    myFinishedEl.hidden = !myFinished;
    oppFinishedEl.hidden = !opponentFinished;
    updateLead();

    // Endless mode only (spec §106-107): the middle slot shows a live
    // "N TO WIN" once the opponent has frozen, from the still-playing side's
    // perspective — recomputed on every own-score change, since the target
    // shrinks as the survivor climbs. Once *I've* frozen there's nothing
    // left for me to do but wait, so it just says so instead.
    function updateTargetDisplay(): void {
      const targetEl = contentEl.querySelector<HTMLSpanElement>('#st-target');
      if (!targetEl) return;
      if (myFinished) {
        targetEl.textContent = opponentFinished ? '' : 'Waiting for opponent…';
      } else if (opponentFinished && opponentFinalScore !== null) {
        const remaining = Math.max(0, opponentFinalScore - myScore + 1);
        targetEl.textContent = remaining > 0 ? `${remaining} TO WIN` : 'AHEAD!';
      } else {
        targetEl.textContent = '';
      }
    }
    updateTargetDisplay();

    // Visual Milestones (spec §51), own tower only — matches Solo Endless's
    // treatment. A fresh timeout id per match start; startLiveMatch always
    // calls clearTimers()/destroyEngines() before rebuilding, so there's no
    // stale timer to leak across a rematch or reconnect rebuild.
    let milestoneTimeoutId: number | undefined;
    function showMilestone(label: string) {
      window.clearTimeout(milestoneTimeoutId);
      milestoneEl.textContent = label;
      milestoneEl.classList.remove('is-showing');
      void milestoneEl.offsetWidth;
      milestoneEl.classList.add('is-showing');
      milestoneTimeoutId = window.setTimeout(() => milestoneEl.classList.remove('is-showing'), 2200);
    }

    if (durationMs !== null) {
      const matchEndsAtMs = matchStartMs + durationMs;
      const timerEl = contentEl.querySelector<HTMLSpanElement>('#st-timer')!;
      liveTimerId = window.setInterval(() => {
        timerEl.textContent = formatClock(matchEndsAtMs - Date.now());
      }, 250);
    }

    opponentEngine = mountOpponentTowerEngine(opponentMount, {
      mode: matchMode,
      onScoreChanged: (score) => {
        opponentScore = score;
        oppScoreEl.textContent = String(score);
        updateLead();
      },
    });
    if (myFinished) {
      // Reconnected after my own run already ended (endless mode only — a
      // finished-but-still-connected player waiting on the opponent is a
      // real, reachable state). The DO only persists my final *score*, not
      // the tower's exact shape, so there's no real geometry to redraw here
      // — deliberately no engine mounted at all rather than one that would
      // incorrectly accept input again over a run that's already over. The
      // score and FINISHED tag (already set above) are what actually matter;
      // an empty own pane for this one reconnect-after-finishing edge case
      // is an accepted, minor cosmetic gap, not a correctness one.
      return;
    }
    ownEngine = mountOwnTowerEngine(ownMount, {
      mode: matchMode,
      matchEndsAtMs: durationMs !== null ? matchStartMs + durationMs : undefined,
      onScoreChanged: (score) => {
        myScore = score;
        myScoreEl.textContent = String(score);
        updateLead();
        if (matchMode === 'endless') updateTargetDisplay();
      },
      onMultiplierChanged: (multiplier) => {
        if (multiplier <= 1) {
          myMultEl.hidden = true;
          return;
        }
        myMultEl.textContent = `×${multiplier}`;
        myMultEl.hidden = false;
        myMultEl.classList.remove('is-pop');
        void myMultEl.offsetWidth;
        myMultEl.classList.add('is-pop');
      },
      onZoneChanged: (zone) => showMilestone(zone.label),
      onPlacement: (event) => send(socket, { type: 'place', event }),
      onMatchEnd: (result) => {
        send(socket, { type: 'final', result });
        if (matchMode === 'endless') {
          // My run is over, but the match might not be — the opponent could
          // still be climbing (or already lost too; either way the server
          // decides and sends `result` when it's actually over). Stay on
          // the live view: freeze my own pane's display, leave the
          // opponent's engine running exactly as it was.
          myFinished = true;
          myFinishedEl.hidden = false;
          updateTargetDisplay();
          return;
        }
        clearTimers();
        destroyEngines();
        uiState = 'waiting-result';
        renderWaitingForResult();
      },
    });
  }

  function renderWaitingForResult(): void {
    contentEl.innerHTML = `<div class="handoff"><p class="handoff__label">Time's up. Waiting for the result…</p></div>`;
  }

  function statBlock(label: string, result: HeadToHeadPlayerResult): string {
    return `
      <div class="round-result">
        <div><span>${escapeHtml(label)}</span><strong>${result.score}</strong></div>
        <div><span>Perfects</span><strong>${result.perfectCount}</strong></div>
        <div><span>Best streak</span><strong>${result.maxPerfectStreak}</strong></div>
        <div><span>Final width</span><strong>${Math.round(result.finalWidthFraction * 100)}%</strong></div>
      </div>
    `;
  }

  function renderResult(msg: ResultMessage): void {
    if (!mySlot) return;
    const mine = msg[mySlot];
    const theirs = msg[mySlot === 'a' ? 'b' : 'a'];
    const label = msg.winner === 'draw' ? 'Draw' : msg.winner === mySlot ? 'You win!' : 'Opponent wins';
    // Empty/absent for a casual match — ranked status is server-derived, never client-declared (D2).
    const myDelta = msg.ratingDeltas?.[mySlot];
    const ratingLine = myDelta !== undefined ? `<p class="handoff__label">Rating ${myDelta >= 0 ? '+' : ''}${myDelta}</p>` : '';
    checkForNewAchievements();
    contentEl.innerHTML = `
      <div class="round-result-pair">
        <p class="handoff__label">${label}</p>
        ${ratingLine}
        <div class="round-result-pair__grid">
          ${statBlock(myName || 'You', mine)}
          ${statBlock(opponentName ?? 'Opponent', theirs)}
        </div>
        <div class="hero-actions" id="st-result-actions">
          <button class="button button--primary" type="button" data-action="rematch" ${opponentPresent ? '' : 'disabled'}>Rematch</button>
          <button class="button button--secondary" type="button" data-action="exit">Back to menu</button>
        </div>
        ${opponentPresent ? '' : '<p class="round-stage__hint">Opponent left. Rematch unavailable.</p>'}
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="rematch"]')?.addEventListener('click', (event) => {
      (event.target as HTMLButtonElement).disabled = true;
      (event.target as HTMLButtonElement).textContent = 'Waiting for opponent…';
      send(socket, { type: 'rematch' });
    });
    contentEl.querySelector<HTMLButtonElement>('[data-action="exit"]')?.addEventListener('click', () => options.onExit());
  }

  function renderOpponentLeft(): void {
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Opponent disconnected</p>
        <button class="button button--secondary" type="button" data-action="exit">Back to menu</button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="exit"]')?.addEventListener('click', () => options.onExit());
  }

  /** C015 Phase 6 — a ranked pair has used their one allowed rematch; both must return to the queue rather than continue in this room. */
  function renderRequeueRequired(message: string): void {
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Rematch unavailable</p>
        <h2>${escapeHtml(message)}</h2>
        <button class="button button--secondary" type="button" data-action="exit">Back to menu</button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="exit"]')?.addEventListener('click', () => options.onExit());
  }

  function handleMessage(msg: ServerMessage): void {
    if (destroyed) return;
    switch (msg.type) {
      case 'joined': {
        mySlot = msg.slot;
        myName = msg.you;
        opponentName = msg.opponent;
        opponentPresent = msg.opponent !== null;
        matchMode = msg.mode;
        if (uiState === 'lobby') renderLobby();
        return;
      }
      case 'opponent-joined': {
        opponentName = msg.name;
        opponentPresent = true;
        if (uiState === 'lobby') renderLobby();
        return;
      }
      case 'resumed': {
        mySlot = msg.slot;
        myName = msg.you;
        opponentName = msg.opponent;
        opponentPresent = msg.opponent !== null;
        matchMode = msg.mode;
        clearDisconnectBanner();
        startLiveMatch(
          msg.matchStartMs,
          matchMode === 'timed' ? msg.durationMs : null,
          { mine: msg.myScore, opponent: msg.opponentScore },
          { mine: msg.myFinished, opponentFinalScore: msg.opponentFinalScore },
        );
        return;
      }
      case 'opponent-disconnected': {
        startDisconnectBanner(msg.graceMs);
        return;
      }
      case 'opponent-reconnected': {
        clearDisconnectBanner();
        return;
      }
      case 'presence': {
        if (mySlot) {
          const oppSlot = mySlot === 'a' ? 'b' : 'a';
          opponentPresent = msg.connected[oppSlot];
          if (msg.names[oppSlot]) opponentName = msg.names[oppSlot];
        }
        // Only ever (re)render the lobby while actually in it — a presence
        // broadcast must never wipe the live match or result screen.
        if (uiState === 'lobby') renderLobby(msg);
        return;
      }
      case 'countdown': {
        clearDisconnectBanner();
        clearTimers();
        destroyEngines();
        uiState = 'countdown';
        // Drive the start off the RELATIVE countdown, not the server's
        // absolute `matchStartMs` compared against our own `Date.now()`. Two
        // devices whose wall clocks differ by even a few seconds otherwise
        // start seconds apart (owner report 2026-08-18: on the same match,
        // desktop ran ~4s ahead of mobile — pure clock skew, since the server
        // hands out an absolute timestamp and each client subtracts its own
        // clock). A locally-derived start is immune to skew; the two clients
        // then differ only by their network-latency delta. The server stays
        // authoritative for finalisation.
        const localMatchStartMs = Date.now() + msg.countdownMs;
        renderCountdown(msg.countdownMs, localMatchStartMs);
        liveTransitionId = window.setTimeout(() => startLiveMatch(localMatchStartMs, msg.durationMs), msg.countdownMs);
        return;
      }
      case 'opponent-place': {
        if (uiState === 'live') opponentEngine?.applyEvent(msg.event);
        return;
      }
      case 'opponent-finished': {
        if (uiState !== 'live') return;
        opponentFinished = true;
        opponentFinalScore = msg.finalScore;
        const oppFinishedEl = contentEl.querySelector<HTMLElement>('#st-opp-finished');
        if (oppFinishedEl) oppFinishedEl.hidden = false;
        const targetEl = contentEl.querySelector<HTMLSpanElement>('#st-target');
        if (targetEl && !myFinished) {
          const remaining = Math.max(0, opponentFinalScore - myScore + 1);
          targetEl.textContent = remaining > 0 ? `${remaining} TO WIN` : 'AHEAD!';
        }
        return;
      }
      case 'result': {
        clearDisconnectBanner();
        clearTimers();
        destroyEngines();
        uiState = 'result';
        renderResult(msg);
        return;
      }
      case 'opponent-left': {
        opponentPresent = false;
        if (uiState === 'lobby') {
          uiState = 'opponent-left';
          renderOpponentLeft();
        } else if (uiState === 'result') {
          // The result itself is already shown and does not change — just
          // reflect that a rematch is no longer possible.
          const rematchButton = contentEl.querySelector<HTMLButtonElement>('[data-action="rematch"]');
          if (rematchButton) rematchButton.disabled = true;
          if (!contentEl.querySelector('[data-role="opponent-left-note"]')) {
            const note = document.createElement('p');
            note.className = 'round-stage__hint';
            note.dataset.role = 'opponent-left-note';
            note.textContent = 'Opponent left. Rematch unavailable.';
            contentEl.querySelector('.round-result-pair')?.appendChild(note);
          }
        }
        return;
      }
      case 'rematch-wanted': {
        if (uiState === 'result' && mySlot && msg.slot !== mySlot) {
          const hint = document.createElement('p');
          hint.className = 'round-stage__hint';
          hint.textContent = 'Opponent wants a rematch.';
          contentEl.querySelector('#st-result-actions')?.insertAdjacentElement('beforebegin', hint);
        }
        return;
      }
      case 'requeue-required': {
        uiState = 'error';
        renderRequeueRequired(msg.message);
        return;
      }
      default:
        return;
    }
  }

  socket.addEventListener('message', (event) => {
    try {
      handleMessage(JSON.parse(event.data as string) as ServerMessage);
    } catch {
      // ignore malformed frames
    }
  });

  return function destroy() {
    destroyed = true;
    clearTimers();
    if (bannerIntervalId !== undefined) window.clearInterval(bannerIntervalId);
    destroyEngines();
  };
}
